import BlockIcon from '@mui/icons-material/Block';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import BuildIcon from '@mui/icons-material/Build';
import CampaignIconOutlined from '@mui/icons-material/CampaignOutlined';
import CodeIcon from '@mui/icons-material/Code';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import OutboxOutlinedIcon from '@mui/icons-material/OutboxOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WebhookIcon from '@mui/icons-material/Webhook';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import TuneIcon from '@mui/icons-material/Tune';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import { Collapse, List, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import Activity from '../activity';
import ActivityCategories from '../activity_categories';
import Airbnb from '../airbnb';
import { userCanAccessAirbnbUi } from '../airbnb/airbnbAccess';
import { DataAPIContext, GET_ONE } from '../api';
import Chat from '../chat';
import Badger from '../chat/components/Badger';
import { getTwilioClient } from '../chat/utils/chat_utils';
import CountBadge from '../components/CountBadge';
import images from '../components/images';
import Explore from '../explore';
import Notification from '../notifications';
import { useOnboardingStatus } from '../onboarding/OnboardingStatus';
import { SYNC_PATH } from '../sync/routes';
import PreTrip from '../pretrip';
import Settings from '../settings';
import { getTrainingInboxCount, setTrainingInboxCount, useTrainingInboxCounts } from '../settings/knowledgeReview/inboxCounts';
import {
    getKnowledgeReviewApi, inboxOperatorFor, isLiveFor, trainingInboxVisibleFor,
} from '../settings/knowledgeReview/knowledgeReviewApi';
import sharedContext from '../sharedContext';
import { ContextStore } from '../store/contextStore';
import Tags from '../tags';
import Trips from '../trips';
import Users from '../users';
import XmlImport from '../xml_import';
import BusTours from '../bus_tours';
import { cv } from '../themeVars';
import { isWebWidgetAvailable } from '../settings/webWidgetAvailable';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        overflowX: 'hidden',
        maxWidth: 360,
        background: theme.palette.background.paper,
        // Less on the right: the scrollbar (when the list overflows) already makes that gap.
        padding: '1rem 0.5rem 1rem 1rem',
        overflowY: 'auto',
        height: 'calc(100svh - 12rem)',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
        },
    },
    listitem_1: {
        fontSize: '10px',
    },

    menu_item_inner: {
        color: `${cv('text', '#344054')} !important`,
        '&:hover': {
            backgroundColor: `${cv('surface-muted', '#F5F5F5')} !important`,
        },
    },
}));

const AI_CHANGES_PATH = '/notifications/ai_changes';

const items_data = [
    // Only while the tenant's easy onboarding is open (self-signup plan §16): `onboardingOnly`.
    { id: 2, title: 'Onboarding', icon: RocketLaunchOutlinedIcon, to: '/onboarding', perms: ['tour_operator.read'], onboardingOnly: true },
    { id: 1, title: 'Chat', icon: Chat.Icon, to: '/chat', perms: ['chat.list'] },
    { id: 5, title: 'Analytics', icon: Settings.SentimentAnalysis.Icon, to: '/analytics', perms: ['sentiment_analysis.list', 'reviews.list', 'agent_metrics.own', 'agent_metrics.guides', 'agent_metrics.all', 'notification.list', 'notification.metrics'] },
    {
        id: 10,
        title: 'My Trips',
        icon: Trips.Icon,
        items: [
            { id: 101, title: 'Trips', icon: Trips.Icon, to: '/trip', perms: ['tour.list'] },
            { id: 102, title: 'Trip Items', icon: PreTrip.Icon, to: '/pretrip/items', perms: ['pre_trip_item.list'] },
            { id: 103, title: 'Activities', icon: Activity.Icon, to: '/activity', perms: ['activity.list'] },
        ],
    },
    {
        id: 15,
        title: 'My Bus Tours',
        icon: BusTours.Icon,
        items: [
            { id: 151, title: 'Line Sets', icon: BusTours.LineSetsIcon, to: '/bus-tours/line-sets', perms: ['vehicle_fleet.list'] },
            { id: 152, title: 'Buses', icon: BusTours.BusesIcon, to: '/bus-tours/vehicles', perms: ['vehicle_fleet.list'] },
            { id: 153, title: 'Attractions', icon: BusTours.AttractionsIcon, to: '/bus-tours/attractions', perms: ['activity.list', 'vehicle_fleet.list'] },
            { id: 154, title: 'Bus Stops', icon: BusTours.BusStopsIcon, to: '/bus-tours/bus-stops', perms: ['vehicle_fleet.list'] },
        ],
    },
    {
        id: 20,
        title: 'Catalog',
        icon: Explore.Icon,
        items: [
            { id: 201, title: 'Itineraries', icon: Explore.Itineraries.Icon, to: '/explore/itineraries', perms: ['itinerary.list'] },
            { id: 202, title: 'Regions', icon: Explore.Regions.Icon, to: '/explore/regions', perms: ['region.list'] },
            { id: 203, title: 'Title Texts', icon: Explore.TitleTexts.Icon, to: '/explore/texts', perms: ['tour_operator.read', 'tour_operator.write'], franchiseScope: 'parent' },
            { id: 204, title: 'Filters', icon: Explore.Filters.Icon, to: '/explore/filters', perms: ['trip_type.list'], franchiseScope: 'parent' },
        ],
    },

    { id: 30, title: 'Users', icon: Users.Icon, to: '/user', perms: ['user.list'] },
    { id: 35, title: 'Live Map', icon: MapOutlinedIcon, to: '/live-map', perms: ['user.list'] },
    // No `perms` on the parent on purpose: campaigns have their own scope now (campaign.list) and
    // Blacklists has always had its own (blacklist_rule.list), so an AND-gate on notification.list
    // would hide the whole section from a campaign-only group. Same pattern as the Ops group —
    // children are filtered first, and the parent auto-hides when none survive.
    { id: 40, title: 'Notifications', icon: Notification.Icon,
        items: [
            { id: 401, title: 'Templates', icon: Notification.Icon, to: '/notifications', perms: ['notification.list'] },
            { id: 404, title: 'Campaigns', icon: CampaignIconOutlined, to: '/notifications/campaigns', perms: ['campaign.list'] },
            { id: 402, title: 'Rulesets', icon: Notification.RulesetIcon, to: '/notifications/rulesets', perms: ['notification.list'] },
            { id: 403, title: 'Strings', icon: Notification.StringIcon, to: '/notifications/strings', perms: ['notification.list'] },
            { id: 405, title: 'Blacklists', icon: BlockIcon, to: '/notifications/blacklists', perms: ['blacklist_rule.list'] },
            // Read-only views of the notification content AI editing works on (metaplan A10).
            { id: 407, title: 'Blocks', icon: WidgetsOutlinedIcon, to: '/notifications/blocks', perms: ['notification.list'] },
            { id: 408, title: 'Block lists', icon: ViewListOutlinedIcon, to: '/notifications/block_lists', perms: ['notification.list'] },
            { id: 409, title: 'Themes', icon: PaletteOutlinedIcon, to: '/notifications/themes', perms: ['notification.list'] },
            { id: 410, title: 'Language policies', icon: TranslateIcon, to: '/notifications/language_policies', perms: ['notification.list'] },
        ]

    },
    // AI changes (client-MCP plan §10.1): the approvals inbox of changes made over MCP, across kinds, and the change
    // sets; it was Notifications › AI changes (406). Its pages keep their /notifications/ai_changes paths (the backend
    // links there, demo/lib/ai_edit/links.py), so `subpaths` highlights it on any of them and groupForPath leaves the
    // Notifications group closed. The grants are explicit-only (Full access does not imply them): explicitPerms. A
    // vocabulary grant opens it too: the inbox lists vocabulary changes to their holders (backend P2-09).
    {
        id: 45,
        title: 'AI changes',
        icon: AutoFixHighOutlinedIcon,
        to: AI_CHANGES_PATH,
        subpaths: true,
        explicitPerms: [
            'notification.mcp_list', 'notification.mcp_read', 'notification.mcp_write', 'notification.mcp_approve',
            'vocabulary.mcp_write', 'vocabulary.mcp_approve',
        ],
    },
    {
        id: 50,
        title: 'Marketing Suite',
        icon: CampaignIconOutlined,
        to: '/marketing-suite/media',
        perms: ['tour_media.list'],
    },
    {
        id: 60,
        title: 'Settings',
        icon: Settings.Icon,
        items: [
            { id: 601, title: 'General', icon: Settings.General.Icon, to: '/settings/general', perms: ['tour_operator.read', 'tour_operator.write'] },
            { id: 614, title: 'Licensees', icon: Settings.Licensees.Icon, to: '/settings/licensees', perms: ['franchise.manager'], franchiseScope: 'parent', franchiseOnly: true },
            { id: 602, title: 'Import', icon: XmlImport.Icon, to: '/import/run/status', exact: true, perms: ['import.list'] },
            { id: 604, title: 'Integrations', icon: XmlImport.IntegrationsIcon, to: '/import/integrations', perms: ['import.integrations'] },
            // The client's own product / aux info syncs (backend self-signup D17) and booking sync (its Bookings card,
            // booking sync plan WP-D): any of the permissions opens the page.
            { id: 620, title: 'Booking-system sync', icon: SyncAltIcon, to: SYNC_PATH, anyPerms: [['import.sync_products'], ['import.sync_aux'], ['ops.sync_bookings']] },
            { id: 606, title: 'Tags', icon: Tags.Icon, to: '/tags', perms: ['tag.list'] },
            { id: 610, title: 'Activity Categories', icon: ActivityCategories.Icon, to: '/activity-categories', perms: ['activity.list'] },
            { id: 607, title: 'Waivers', icon: MenuBookIcon, to: '/waivers', perms: ['waiver.list'] },
            { id: 608, title: 'Pre-check-in', icon: Settings.General.Icon, to: '/settings/checkin', perms: ['tour_operator.read', 'tour_operator.write'] },
            { id: 609, title: 'AI Suite', icon: Settings.TrainingMaterials.Icon, to: '/settings/ai', perms: ['ai.read', 'ai.list'] },
            // Client-MCP Q26: the MCP auto-apply switch; ai_edit.manage is explicit-only (Full access does not imply it).
            { id: 621, title: 'AI editing', icon: TuneIcon, to: '/settings/ai-editing', explicitPerms: ['ai_edit.manage'] },
            // Client-MCP §12.5: the company's words its AI client reads; the vocabulary grants are explicit-only.
            { id: 622, title: 'AI vocabulary', icon: SpellcheckIcon, to: '/settings/ai-vocabulary', explicitPerms: ['vocabulary.mcp_write', 'vocabulary.mcp_approve'] },
            { id: 615, title: 'Web Chat Widget', icon: Settings.WebWidget.Icon, to: '/settings/web-widget', perms: ['web_widget.read'] },
            { id: 612, title: 'Tour chat priority', icon: Settings.TourChatPriority.Icon, to: '/settings/tour-chat-priority', perms: ['tour.read'] },
            // Sync Bookings, Split OTA Bookings and Import OTAs live under Ops only (701-703, the ops.* permissions their
            // endpoints check). Their Settings copies (611 / 613 / 617) were gated on import.* permissions the backend
            // never checks, and showed the same page twice (booking sync plan D4, 2026-10-31).
            { id: 618, title: 'Airbnb', icon: Airbnb.Icon, to: '/settings/airbnb' },
            // CP-7402: shown by the server-decided capability (permissions.email_domains), not an
            // RBAC perm -- Curated Planet staff only for now; a backend setting opens it to operators.
            { id: 619, title: 'Email Domain', icon: Settings.EmailDomains.Icon, to: '/settings/email-domain' },

            //{ id: 607, title: 'Reviews', icon: Settings.Reviews.Icon, to: '/settings/reviews', perms: ['reviews.list'] },
            //{ id: 608, title: 'Analytics', icon: Settings.SentimentAnalysis.Icon, to: '/settings/sentimentanalysis', perms: ['sentiment_analysis.list'] },
        ],
    },
    {
        // Developer (backend metaplan S7b, SBX plan D9): one section per dashboard, production's and
        // the sandbox's. No `perms` on the parent: it shows when any child survives. Children marked
        // `environment: 'sandbox'` exist only on a sandbox (bootstrap `environment`). API keys and
        // Documentation moved here from Settings (old routes redirect); `anyPerms` keeps them for
        // users who had them there (import.list + import.dev) without a developer.* permission.
        id: 80,
        title: 'Developer',
        icon: CodeIcon,
        items: [
            { id: 801, title: 'Overview', icon: DashboardOutlinedIcon, to: '/developer/overview', perms: ['developer.read'] },
            // DSEC INT: the booking-system and fleet connections. Its editors hold import.integrations (Settings ›
            // Integrations, 604, redirects here), so either permission shows it.
            { id: 810, title: 'Integrations', icon: XmlImport.IntegrationsIcon, to: '/developer/integrations', anyPerms: [['developer.read'], ['import.integrations']] },
            { id: 802, title: 'Request log', icon: ListAltIcon, to: '/developer/requests', perms: ['developer.read'] },
            { id: 803, title: 'Outbox', icon: OutboxOutlinedIcon, to: '/developer/outbox', perms: ['developer.read'], environment: 'sandbox' },
            { id: 804, title: 'Webhooks', icon: WebhookIcon, to: '/developer/webhooks', perms: ['developer.read'] },
            { id: 805, title: 'Vendor calls', icon: StorefrontOutlinedIcon, to: '/developer/vendor-calls', perms: ['developer.read'], environment: 'sandbox' },
            { id: 806, title: 'Metrics', icon: QueryStatsIcon, to: '/developer/metrics', perms: ['developer.read'] },
            { id: 807, title: 'Activity', icon: NotificationsOutlinedIcon, to: '/developer/activity', perms: ['developer.read'] },
            { id: 808, title: 'API keys', icon: XmlImport.KeyIcon, to: '/developer/api-keys', anyPerms: [{ perms: ['developer.keys_manage'], environment: 'sandbox' }, ['api_credential.list'], ['import.list', 'import.dev']] },
            { id: 809, title: 'Documentation', icon: MenuBookIcon, to: '/developer/docs', anyPerms: [['developer.read'], ['import.list', 'import.dev']] },
        ],
    },
    {
        // Ops parent has no `perms` of its own — children are filtered by their
        // individual `ops.*` perms first, and the parent then falls through to
        // the `items.length > 0` rule, so it auto-hides for users without any
        // ops permission.
        id: 70,
        title: 'Ops',
        icon: BuildIcon,
        items: [
            { id: 701, title: 'Sync Bookings', icon: Settings.SyncBookings.Icon, to: '/settings/sync-bookings', perms: ['ops.sync_bookings'] },
            { id: 704, title: 'Sync Orders', icon: Settings.SyncOrders.Icon, to: '/settings/sync-orders', perms: ['ops.sync_orders'] },
            { id: 702, title: 'Split OTA Bookings', icon: Settings.SplitOTABookings.Icon, to: '/settings/split-ota-bookings', perms: ['ops.split_ota_bookings'] },
            { id: 703, title: 'Import OTAs', icon: XmlImport.Icon, to: '/import/otas', perms: ['ops.import_otas'], franchiseScope: 'parent' },
        ],
    },
];

const OPS_ROUTE_PREFIXES = [
    '/settings/sync-bookings',
    '/settings/sync-orders',
    '/settings/split-ota-bookings',
    '/import/otas',
];

// Which top-level group a route lives under, so deep-linking into a child
// opens its parent. Ops routes are matched first — they overlap the broad
// settings/import prefixes.
const groupForPath = (pathname) => {
    if (OPS_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))) {
        return 70;
    }
    // A top-level entry under the /notifications prefix.
    if (pathname === AI_CHANGES_PATH || pathname.startsWith(`${AI_CHANGES_PATH}/`)) {
        return null;
    }
    const segment = pathname.split('/')[1];
    if (['import', 'settings', 'tags', 'activity-categories', 'waivers'].includes(segment)) {
        return 60;
    }
    if (['activity', 'trip', 'pretrip'].includes(segment)) {
        return 10;
    }
    if (segment === 'bus-tours') {
        return 15;
    }
    if (segment === 'explore') {
        return 20;
    }
    if (segment === 'notifications') {
        return 40;
    }
    if (segment === 'marketing-suite') {
        return 50;
    }
    if (segment === 'developer') {
        return 80;
    }
    return null;
};


const isItemSelected = (item, selected) => {
    const normalizedSelected = selected.toLowerCase();
    const cleanedSelected = normalizedSelected.replace(/\/\d+$/, ''); // Remove trailing / and numbers

    // Check for direct match or sub-item match
    if (item.items) {
        for (let index = 0; index < item.items.length; index++) {
            const subItem = item.items[index];
            const subItemPath = subItem.to.toLowerCase();

            // Direct match or partial match for nested paths
            if (cleanedSelected.startsWith('/notifications')) {
                if (cleanedSelected === subItemPath) {
                    return true;
                }
            } else if (cleanedSelected.startsWith(subItemPath) || 
                (subItemPath.split('/').length > 2 && cleanedSelected.includes(subItemPath))) {
                return true;
            }            
        }
    }

    // Checking the main item
    if (item.to) {
        const itemPath = item.to.toLowerCase();
        // Direct match or leading path match (accounting for nested URLs)
        if (cleanedSelected.startsWith('/notifications')) {
            if (cleanedSelected === itemPath || (item.subpaths && cleanedSelected.startsWith(`${itemPath}/`))) {
                return true;
            }
        } else if (cleanedSelected === itemPath || 
            cleanedSelected.startsWith(itemPath + '/') ||
            (itemPath.split('/').length > 2 && cleanedSelected.includes(itemPath))) {
            return true;
        }
    }

    return false;
};

const MenuItem = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMenuItemSelected = isItemSelected(props.item, props.selected);
    // Plain hex in light mode rather than cv(): jsdom drops var() from inline styles,
    // and menu.test.js reads this background to tell which row is highlighted.
    const selectedBackground = theme.palette.mode === 'light' ? '#F5F5F5' : 'var(--cp-surface-muted)';
    const itemRef = React.useRef(null);

    // CP-8290: the list starts scrolled to the top, so a selection deep in a
    // long group (e.g. a Settings sub-menu on mobile) can sit below the fold
    // when the drawer opens. Scroll the group header to the top so the whole
    // group shows, then nudge the selected leaf visible for groups longer
    // than one screen. Only leaves trigger this — a group header also counts
    // as "selected" when one of its children matches, and it must not compete.
    React.useEffect(() => {
        if (isMenuItemSelected && props.isDrawerOpen && props.itemChildrenLength === 0) {
            if (props.groupHeaderId) {
                document.getElementById(props.groupHeaderId)?.scrollIntoView({ block: 'start' });
            }
            itemRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [isMenuItemSelected, props.isDrawerOpen, props.itemChildrenLength, props.groupHeaderId]);

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => (
                <Link ref={ref} to={props.item.to} style={{ textDecoration: 'none', display: 'block', minWidth: 0 }}>
                    <Typography noWrap fontWeight={400} style={{ color: cv('text', '#344054'), textDecoration: 'inherit !important', paddingTop: '0.05rem' }}>
                        {props.item.title}
                    </Typography>
                </Link>
            )),
        [props.item.to]
    );


    return (
        <div
            key={props.item.id}
            id={`main-menu-item-${props.item.id}`}
            ref={itemRef}
            onClick={(ev) => props.onClick(props.item, props.level, ev)}
            className={classes.menu_item_outer}
            style={{ cursor: 'pointer', padding: props.isDrawerOpen ? '0px 0.5rem 2px 0.5rem' : '0px 5px 2px 5px', display: 'flex', alignItems: 'center', paddingLeft: props.isDrawerOpen ? (props.level > 0 ? '30px' : '8px') : '5px' }}
        >
            <div className={classes.menu_item_inner} style={{ backgroundColor: isMenuItemSelected && selectedBackground, borderRadius: '8px', height: '40px', width: '100%', display: 'flex', justifyContent: props.isDrawerOpen ? 'space-between' : 'center', alignItems: 'center' }}>
                <div style={{ display: 'flex', justifyContent: props.isDrawerOpen ? 'flex-start' : 'center', alignItems: 'center', gap: props.isDrawerOpen ? '0.5rem' : '0', paddingLeft: props.isDrawerOpen ? '0.5rem' : 0, minWidth: 0 }}>
                    <div style={{ paddingTop: '3px' }}>{props.icon}</div>
                    {props.isDrawerOpen && (
                        <div style={{ minWidth: 0 }} title={props.item.title}>
                            {props.item.to ? (
                                <CustomLink to={props.item.to} />
                            ) : (
                                <Typography
                                    noWrap
                                    style={{
                                        color: cv('text', '#344054'),
                                            fontSize: 16,
                                    }}
                                // fontWeight={props.level === 0 && isMenuItemSelected && 600}
                                >
                                    {props.item.title}
                                </Typography>
                            )}
                        </div>
                    )}
                    {/* Count badges beside the label, flex-centred so they line up with the label's
                        centre instead of sitting on a text line box. A long label truncates rather
                        than pushing its badge past the edge. */}
                    {props.item.to === '/chat' && props.isDrawerOpen && props.unreadMsgCount > 0 && (
                        <div style={{ display: 'flex', flexShrink: 0 }}>
                            <MenuCountBadge count={props.unreadMsgCount} />
                        </div>
                    )}
                    {props.badgeCount > 0 && props.isDrawerOpen && (
                        <div style={{ display: 'flex', flexShrink: 0 }}>
                            <MenuCountBadge count={props.badgeCount} title={props.badgeTitle} />
                        </div>
                    )}
                </div>
                <div style={props.isDrawerOpen ? { display: 'flex', alignItems: 'center', flexShrink: 0 } : undefined}>
                    {!props.isDrawerOpen && (props.item.to === '/chat' ? <MenuCountBadge count={props.unreadMsgCount} small /> : '')}
                    {!props.isDrawerOpen && props.badgeCount > 0 && <MenuCountBadge count={props.badgeCount} small title={props.badgeTitle} />}
                    {props.isDrawerOpen && props.itemChildrenLength > 0 && props.open && (
                        <div style={{ display: 'flex', paddingRight: '4px' }}>
                            <ExpandLessIcon />
                        </div>
                    )}
                    {props.isDrawerOpen && props.itemChildrenLength > 0 && !props.open && (
                        <div style={{ display: 'flex', paddingRight: '4px' }}>
                            <ExpandMoreIcon />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Menu = (props) => {
    const classes = useStyles();
    // CP-8290: seeded from the URL rather than set in a mount effect. On
    // mobile the Drawer is a Modal that unmounts the menu every time it
    // closes, so an effect-driven setOpen made the group's Collapse animate
    // from height 0 on every open — the selected item's scrollIntoView then
    // ran while the list had nothing to scroll and landed at the top.
    // CP-8425: the selection comes from the router, not from a prop. App.js mounts one
    // MainLayout per top-level <Route>, so moving between pages under the same route
    // (e.g. /bus-tours/line-sets -> /bus-tours/vehicles) never re-renders the layout, and a
    // path it passed down stayed on the page the route was entered on.
    const { pathname: selected } = useLocation();
    const [open, setOpen] = React.useState(() => groupForPath(selected));
    const [unreadMsgCountState, setUnreadMsgCount] = React.useState(0);
    const unreadMsgCount = React.useRef(0);
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const { tourOperator, settings, user, hasPerm, hasAllPerms, hasExplicitPerm, isFranchise, licensees, userLicenseeIds, currentLicensee, environment } = React.useContext(ContextStore);
    const { status: onboardingStatus } = useOnboardingStatus();
    const isLicenseeOnlyUser = isFranchise && userLicenseeIds.length > 0 && !hasPerm('franchise', 'manager');
    // `explicitPerms`: shown when ANY of these is explicitly granted (hasExplicitPerm ignores Full access).
    const hasAnyExplicitPerm = (list) => list.some((spec) => {
        const [scope, action] = spec.split('.', 2);
        return typeof hasExplicitPerm === 'function' && hasExplicitPerm(scope, action);
    });

    const items = items_data.map(item => {
        const ni = { ...item };
        if (item.items && Array.isArray(item.items)) {
            ni.items = item.items.filter((subItem) => {
                if (subItem.franchiseScope === 'parent' && isLicenseeOnlyUser) {
                    return false;
                }
                // Full-access admins of standalone operators hold franchise.manager too,
                // so the permission alone would show this where it cannot work (CP-8424).
                if (subItem.franchiseOnly && !isFranchise) {
                    return false;
                }
                if (subItem.explicitPerms && !hasAnyExplicitPerm(subItem.explicitPerms)) {
                    return false;
                }
                // A session without `environment` (stored before the backend sent it) reads as production.
                if (subItem.environment && subItem.environment !== (environment || 'production')) {
                    return false;
                }
                if (subItem.anyPerms) {
                    // An entry is a permission list, or {perms, environment} for one that counts on that
                    // environment only (developer.keys_manage: a sandbox's, DSEC D1).
                    return subItem.anyPerms.some((entry) => (Array.isArray(entry)
                        ? hasAllPerms(entry)
                        : entry.environment === (environment || 'production') && hasAllPerms(entry.perms)));
                }
                if (subItem.id === 612) {
                    return Boolean(tourOperator?.config?.enable_tour_chat_priority)
                        && (subItem.perms && subItem.perms.length > 0 ? hasAllPerms(subItem.perms) : true);
                }
                if (subItem.id === 615) {
                    // Web Widget: only when the feature is enabled for the operator.
                    return isWebWidgetAvailable(tourOperator)
                        && (subItem.perms && subItem.perms.length > 0 ? hasAllPerms(subItem.perms) : true);
                }
                if (subItem.id === 619) {
                    return Boolean(user?.permissions?.email_domains?.read);
                }
                if (subItem.id === 618) {
                    // Airbnb onboarding is gated to internal staff email today
                    // (same gate as the page + backend). Hide for everyone else.
                    return userCanAccessAirbnbUi()
                        && (subItem.perms && subItem.perms.length > 0 ? hasAllPerms(subItem.perms) : true);
                }
                return subItem.perms && subItem.perms.length > 0 ? hasAllPerms(subItem.perms) : true;
            });
        }
        return ni;
    }).filter((item) => {
        if (item.onboardingOnly && onboardingStatus !== 'open') {
            return false;
        }
        if (item.explicitPerms && !hasAnyExplicitPerm(item.explicitPerms)) {
            return false;
        }
        // hack for analytics (show if the user can see any Analytics tab:
        // Sentiment Analysis, Ratings, Agent Metrics, or Notification Metrics)
        if (item.id === 5){
            return hasPerm('sentiment_analysis', 'list')
                || hasPerm('reviews', 'list')
                // CP-8224: Agent Metrics rides its own scope, not chat.list.
                || hasPerm('agent_metrics', 'own')
                || hasPerm('agent_metrics', 'guides')
                || hasPerm('agent_metrics', 'all')
                || hasPerm('notification', 'list')
                || hasPerm('notification', 'metrics')
        }
        return (item.perms && item.perms.length > 0) ? hasAllPerms(item.perms) : (item.to || (item.items && item.items.length > 0));
    });

    // AI Training Inbox badges on AI Suite (support inbox) and Web Chat Widget (widget
    // inbox): the same open-card counts as the inbox tabs, whenever those rows are shown
    // and the inbox is visible to this operator. The tabs publish their counts as agents
    // work through cards, so the menu follows along without refetching.
    const visibleSettingsIds = new Set((items.find((item) => item.id === 60)?.items || []).map((sub) => sub.id));
    // A franchise switches the inbox on per licensee: the sidebar's licensee decides.
    const inboxOperator = inboxOperatorFor(tourOperator, isFranchise, licensees, currentLicensee);
    const inboxVisible = trainingInboxVisibleFor(inboxOperator);
    const inboxChannels = inboxVisible
        ? [visibleSettingsIds.has(609) && 'support', visibleSettingsIds.has(615) && 'widget'].filter(Boolean)
        : [];
    const inboxTopId = (isFranchise && currentLicensee) ? currentLicensee : tourOperator?.id;
    const inboxLive = isLiveFor(inboxOperator);
    const inboxChannelsKey = inboxChannels.join(',');
    const inboxCounts = useTrainingInboxCounts();
    const badgeCounts = {
        609: inboxChannels.includes('support') ? getTrainingInboxCount(inboxCounts, inboxTopId, 'support') : null,
        615: inboxChannels.includes('widget') ? getTrainingInboxCount(inboxCounts, inboxTopId, 'widget') : null,
    };

    React.useEffect(() => {
        if (!inboxTopId || !inboxChannelsKey) return undefined;
        let active = true;
        const refresh = () => {
            const api = getKnowledgeReviewApi(dataAPI, inboxTopId, inboxLive);
            inboxChannelsKey.split(',').forEach((channel) => {
                api.summary(channel)
                    .then((s) => { if (active) setTrainingInboxCount(inboxTopId, channel, s?.open ?? null); })
                    .catch(() => { });
            });
        };
        refresh();
        // New cards arrive from the backend, not from this tab; pick them up now and then.
        const timer = setInterval(refresh, 10 * 60 * 1000);
        return () => {
            active = false;
            clearInterval(timer);
        };
    }, [dataAPI, inboxTopId, inboxLive, inboxChannelsKey]);

    const img = images.url(settings.images.base, tourOperator.logo, 'cover_16x16');
    const myBadgerOptions = { src: img }; // See: constructor for customization options
    const myBadger = new Badger(myBadgerOptions);

    const handleClick = (item, level, ev) => {
        if (level === 0) {
            if (item.items) {
                if (open === item.id) {
                    setOpen(null);
                } else {
                    setOpen(item.id);
                }
            } else {
                setOpen(null);
            }
        }
        if (props.isSmallScreen && !item.items) {
            props.setIsDrawerOpen(false);
        }
        item.to && history.push(item.to);
    };

    const item2ListItem = (item, itemidx, level, parentId) => {
        const itemChildren = (item.items || []).map((ii, iidx) => item2ListItem(ii, iidx, level + 1, item.id));
        // the color of the icon that are imported from MUI should be the same local icons in /icons/main_menu which were imported from Figma with the following color '#344054'
        const icon = <item.icon style={{ color: cv('text-secondary', '#344054') }} />;
        // A group carries its rows' badges while the rows are hidden (group closed, or the
        // drawer collapsed to icons), so the to-do is visible from anywhere in the menu.
        const childrenHidden = !props.isDrawerOpen || open !== item.id;
        const badgeCount = item.items
            ? (childrenHidden ? item.items.reduce((sum, sub) => sum + (badgeCounts[sub.id] || 0), 0) : 0)
            : (badgeCounts[item.id] || 0);
        const badgeTitle = badgeCount > 0 ? `${badgeCount} to review in the AI Training Inbox` : undefined;
        return (
            <React.Fragment key={item.id}>
                <MenuItem itemChildrenLength={itemChildren.length} item={item} open={open === item.id} level={level} isDrawerOpen={props.isDrawerOpen} icon={icon} onClick={handleClick} unreadMsgCount={unreadMsgCountState} badgeCount={badgeCount} badgeTitle={badgeTitle} selected={selected} groupHeaderId={parentId != null ? `main-menu-item-${parentId}` : undefined} />
                {itemChildren && props.isDrawerOpen ? (
                    <Collapse key={`${item.id}-children`} component="li" in={open === item.id} timeout="auto" unmountOnExit>
                        <List disablePadding>{itemChildren}</List>
                    </Collapse>
                ) : null}
            </React.Fragment>
        );
    };

    const filteredItems = items.filter((item) => {
        const enableChat = true;
        if (item.tour_operators && item.tour_operators.length > 0) {
            return item.tour_operators.indexOf(tourOperator?.code) >= 0;
        }
        return enableChat || !item.to || item.to.indexOf('/chat') !== 0;
    });

    const updateUnreadMessagesCount = async () => {
        const res = await dataAPI(GET_ONE, 'chat/unread_msg_count', {});

        unreadMsgCount.current = res.data.unread_message_count;
        myBadger.value = unreadMsgCount.current;
        setUnreadMsgCount(unreadMsgCount.current);
        sharedContext.set('unreadMsgCount', unreadMsgCount.current);

        console.debug('menu: updateUnreadMessagesCount: Updated to ', unreadMsgCount.current);
    };

    React.useEffect(() => {
        let client = null;
        (async () => {
            updateUnreadMessagesCount();
            console.log('ChatList: Getting chat client...');
            client = await getTwilioClient();
            console.log('ChatList: Client loaded!');

            client?.on('messageAdded', messageAddedHandler);
            client?.on('participantUpdated', participantUpdatedHandler);
        })();

        return () => {
            console.debug('menu: Cleaning up chat client listeners');
            client?.removeListener('messageAdded', messageAddedHandler);
            client?.removeListener('participantUpdated', participantUpdatedHandler);
        };
    }, []);

    const messageAddedHandler = async (data) => {
        console.debug('menu: messageAddedHandler: message added!', unreadMsgCount.current);
        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
        if (!bootstrapUser) return;
        const tw_identity = bootstrapUser['twilio_identity'];
        const msg_author = data.state.author;
        if (msg_author !== tw_identity) {
            // uncomment to make the badge update a little quicker
            // unreadMsgCount.current = unreadMsgCount.current + 1
            // setUnreadMsgCount(unreadMsgCount.current)
            handleUnreadCounterChanges();
        }
    };

    const participantUpdatedHandler = async (data) => {
        const bootstrapUser = JSON.parse(window.localStorage.getItem('bootstrap'))?.user;
        // Signed out (bootstrap wiped, e.g. by a logout in another tab): this tab's chat client is stale.
        if (!bootstrapUser) return;
        const tw_identity = bootstrapUser['twilio_identity'];
        const updated_participant_identity = await data.participant.identity;
        console.debug('menu: participantUpdatedHandler: participant updated!', data, tw_identity, updated_participant_identity);
        if (updated_participant_identity === tw_identity) {
            handleUnreadCounterChanges();
        }
    };

    const handleUnreadCounterChanges = async () => {
        console.debug('menu: handleUnreadCounterChanges: updating counter');
        // add a small delay to give our backend time to process the new message
        setTimeout(
            () =>
                (async () => {
                    updateUnreadMessagesCount();
                })(),
            500
        );
        // in case it takes extra long, add an additional refresh for insurance
        setTimeout(
            () =>
                (async () => {
                    updateUnreadMessagesCount();
                })(),
            1500
        );
        // in case it takes EXTRA long, add an additional refresh for insurance
        setTimeout(
            () =>
                (async () => {
                    updateUnreadMessagesCount();
                })(),
            5000
        );
    };

    return (
        <List className={classes.root} key="root">
            {filteredItems.map((item, itemidx) => item2ListItem(item, itemidx, 0))}
        </List>
    );
};

// Menu badges: full size 12px from the label (the row's 8px gap + 4px; 16px set it apart,
// 8px crowded it); small over the icon's corner in the collapsed drawer. Full size sits 0.7px above the row's centre: centred on the row it lines up with the
// label's lowercase, 1.4px below its capitals (measured 9/26), so this splits the difference.
// A transform, because a fractional `top` is snapped back to a whole pixel.
const MenuCountBadge = ({ count, small = false, title }) => (small ? (
    <CountBadge count={count} title={title} size={15} fontSize=".64rem" pillPadding={4}
        style={{ marginLeft: '-12px', marginRight: '-16px', marginTop: '-4px' }} />
) : (
    <CountBadge count={count} title={title} style={{ marginLeft: 4, transform: 'translateY(-0.7px)' }} />
));

export default Menu;
